import { apiClient } from '../httpClient';
import { handleApiError } from '../utils/errorHandler';

const API_ENDPOINTS = {
  REQUEST_CANDIDATES: '/production/work_order/v1/winding_request/candidates',
  REQUEST_BASE: '/production/work_order/v1/winding_request/',
  REQUEST_STATUS: '/production/work_order/v1/winding_request/status',
  PROCESS_ITEM: '/mes/winding/v1/process/item',
  PROCESS_LIST: '/mes/winding/v1/process/list',
  PROCESS_LIST_BASE: '/mes/winding/v1/process/list_base',
  PROCESS_LIST_BY_IDS: '/mes/winding/v1/process/list_by_ids',
  PROCESS_STATUS_LIST: '/java/mes/winding/v1/process/status_list',
  PROCESS_STATUS_SUMMARY: '/java/mes/winding/v1/process/status_list/summary',
  EVENTS: '/mes/events/v1/'
};

const formatTargetDate = (date) => `${date} 00:00:00`;

// 동일한 파라미터로 중복 호출되는 것을 방지하기 위한 Promise 캐시
let pendingRequestCandidatesPromise = null;
let pendingRequestCandidatesKey = null;

const getParamsKey = (params = {}) => {
  try {
    return JSON.stringify(params);
  } catch (e) {
    // 직렬화 실패 시 항상 새로운 요청으로 처리
    return `${Date.now()}_${Math.random()}`;
  }
};

const createApiCall = async (method, endpoint, data = null, params = null, errorMessage = '') => {
  try {
    const config = {};
    if (params) config.params = params;
    
    let response;
    switch (method) {
      case 'GET':
        response = await apiClient.get(endpoint, config);
        break;
      case 'POST':
        response = await apiClient.post(endpoint, data, config);
        break;
      case 'PUT':
        response = await apiClient.put(endpoint, data, config);
        break;
      case 'DELETE':
        response = await apiClient.delete(endpoint, config);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    throw new Error(errorMessage || response.data);
  } catch (error) {
    return handleApiError(error, errorMessage);
  }
};

export const windingService = {
  getWindingRequestCandidates: (params = {}) => {
    const key = getParamsKey(params);

    // 같은 파라미터로 이미 진행 중인 요청이 있다면 그 Promise 재사용
    if (pendingRequestCandidatesPromise && pendingRequestCandidatesKey === key) {
      return pendingRequestCandidatesPromise;
    }

    pendingRequestCandidatesKey = key;
    pendingRequestCandidatesPromise = createApiCall(
      'GET',
      API_ENDPOINTS.REQUEST_CANDIDATES,
      null,
      params
    ).finally(() => {
      // 해당 키에 대한 요청이 끝나면 캐시 초기화
      if (pendingRequestCandidatesKey === key) {
        pendingRequestCandidatesPromise = null;
        pendingRequestCandidatesKey = null;
      }
    });

    return pendingRequestCandidatesPromise;
  },

  createWindingRequest: (requestData) => 
    createApiCall('POST', API_ENDPOINTS.REQUEST_BASE, requestData),

  updateWindingProcess: (requestData) => 
    createApiCall('PUT', `${API_ENDPOINTS.PROCESS_ITEM}/${requestData.winding_id}`, requestData),

  updateTargetDate: (windingId, newDate) => {
    const data = {
      winding_id: windingId,
      target_date: formatTargetDate(newDate)
    };
    return createApiCall('PUT', `${API_ENDPOINTS.PROCESS_ITEM}/${windingId}`, data);
  },

  updatePlanDate: (windingId, newDate) => {
    const data = {
      winding_id: windingId,
      date_planned: formatTargetDate(newDate)
    };
    return createApiCall('PUT', `${API_ENDPOINTS.PROCESS_ITEM}/${windingId}`, data);
  },

  deleteWindingWork: (windingId) => 
    createApiCall('DELETE', `${API_ENDPOINTS.PROCESS_ITEM}/${windingId}`),

  updateWindingStatus: (itemsToUpdate) => 
    createApiCall('POST', API_ENDPOINTS.REQUEST_STATUS, itemsToUpdate),

  getWindingWork: (params = {}) => 
    createApiCall('GET', API_ENDPOINTS.PROCESS_LIST, null, params),

  getWindingStatusList: (params = {}) =>
    createApiCall('GET', API_ENDPOINTS.PROCESS_STATUS_LIST, null, params, 'Failed to fetch winding status list'),

  getWindingStatusSummary: (params = {}) =>
    createApiCall('GET', API_ENDPOINTS.PROCESS_STATUS_SUMMARY, null, params, 'Failed to fetch winding status summary'),

  /** 와인딩 프로세스 목록만 조회 (날짜/타겟 필터). sub_order 상세는 별도 by-ids 호출 */
  getWindingProcessListBase: (params = {}) =>
    createApiCall('GET', API_ENDPOINTS.PROCESS_LIST_BASE, null, params),

  /** winding_id 목록으로 해당 행의 프로세스만 조회 (LINE/OPERATION 갱신용). body는 배열로 전송 */
  getWindingProcessListByIds: (windingIdList = []) =>
    createApiCall('POST', API_ENDPOINTS.PROCESS_LIST_BY_IDS, Array.isArray(windingIdList) ? windingIdList : []),

  createEvent: (eventData) => 
    createApiCall('POST', API_ENDPOINTS.EVENTS, eventData, null),

  // 와인딩 요청 일별 현황 조회
  fetchDailyStatus: async (params, t) => {
    const endpoint = '/production/work_order/v1/winding_request/daily-status';
    try {
      // params의 start_datetime, end_datetime은 필수 파라미터
      if (!params.start_datetime || !params.end_datetime) {
        throw new Error('start_datetime and end_datetime are required');
      }

      const response = await apiClient.get(endpoint, { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, t ? t('winding.errors.requestFailed') : 'Request failed');
    }
  }
}; 